import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API } from '@/utils/API';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import SubmissionTemplateBreadcrumbs from './components/SubmissionTemplateBreadcrumbs';
import EditorPanel from './EditorPanel';
import HistoryPanel from './HistoryPanel';
import ReadOnlyEditorPanel from './ReadOnlyEditorPanel';
import { useUserOptional } from '@/providers/UserProvider';

interface Template {
  id: string;
  category: string;
  sub_category: string;
  variant: string;
  form_type: string;
  status: string;
  csv_id: number;
}

type WorkspaceTab = 'edit' | 'history';

interface WorkspaceProps {
  tab: WorkspaceTab;
}

// Merged "template workspace" — the Editor and History tabs are two views of
// the same template (its editable draft, and its published/archived version
// timeline), replacing what used to be two separate full-page routes
// (Edit.tsx, Versions.tsx) reachable only via a one-way link and a full page
// reload. Switching tabs here is a client-side route change (/edit <->
// /history), so it keeps browser back/forward and is bookmarkable, without
// reloading the page.
const Workspace: React.FC<WorkspaceProps> = ({ tab }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const userContext = useUserOptional();
  // False while templates are managed in code (the write endpoints 403).
  // Wait for /users/me before choosing a panel: EditorPanel opens a draft
  // (a POST) the moment it mounts.
  const userLoading = userContext?.loading ?? false;
  const canEdit = userContext?.userData?.can_edit_submission_templates ?? false;

  const [template, setTemplate] = useState<Template | null>(null);

  useEffect(() => {
    if (!id) return;
    API.authenticated.admin
      .fetch(`/submission_templates/${id}`)
      .then(r => (r.ok ? r.json() : null))
      .then((data: Template | null) => setTemplate(data))
      .catch(() => setTemplate(null));
  }, [id]);

  if (!id) return null;

  const handleTabChange = (value: string) => {
    navigate(`/admin_workflows/submission_templates/${id}/${value}`);
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50 font-sans text-sm">
      <div className="bg-white border-b px-5 py-3 flex items-center justify-between shrink-0">
        <SubmissionTemplateBreadcrumbs template={template} />
        <a
          href="/admin_workflows/submission_templates"
          className="text-xs text-gray-500 hover:text-gray-800 border border-gray-200 rounded px-3 py-1.5"
        >
          ← All templates
        </a>
      </div>

      <div className="bg-white border-b px-5 pt-2 shrink-0">
        <Tabs value={tab} onValueChange={handleTabChange}>
          <TabsList>
            <TabsTrigger value="edit">Editor</TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {!userLoading && !canEdit && (
        <div className="bg-amber-50 border-b border-amber-200 px-5 py-2.5 text-xs text-amber-800 shrink-0">
          Forms are managed in code. Manual editing is turned off, so this view
          is read-only.
        </div>
      )}

      <div className="flex-1">
        {userLoading ? null : tab === 'edit' && !canEdit ? (
          <ReadOnlyEditorPanel templateId={id} />
        ) : tab === 'edit' ? (
          <EditorPanel
            templateId={id}
            onPublished={() => {
              /* Stay on Editor tab — EditorPanel shows its own
                 "start a new draft" prompt after a successful publish. */
            }}
          />
        ) : (
          <HistoryPanel
            templateId={id}
            onDraftOpened={() => handleTabChange('edit')}
            readOnly={!canEdit}
          />
        )}
      </div>
    </div>
  );
};

export default Workspace;
